import { adminService } from "@/api/admin/admin.service";
import type { TWhatsAppGroupListItem } from "@/api/admin/admin.types";
import {
  DataGrid,
  type TColumnDef,
  type TDataGridQuery,
} from "@/components/data-grid";
import { useToast } from "@/components/ui";
import { Copy } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";

const PAGE_SIZE = 20;

export function WhatsAppGroupsPage() {
  const { toast } = useToast();
  const navigate = useNavigate();

  const fetcher = async (query: TDataGridQuery) => {
    const response = await adminService.getWhatsAppGroups(
      {
        limit: query.limit,
        offset: query.offset,
        ...(query.search ? { search: query.search } : {}),
        ...(query.sort_by ? { sort_by: query.sort_by, sort_order: query.sort_order } : {}),
      },
      { signal: query.signal }
    );
    if (response.error) throw response.error;
    return response.data;
  };

  const copyInviteLink = (link: string) => {
    navigator.clipboard.writeText(link).then(
      () => toast({ variant: "success", title: "Link copied", description: "Invite link copied to clipboard." }),
      () =>
        toast({
          variant: "destructive",
          title: "Copy failed",
          description: "Unable to copy the link. Please copy it manually.",
        })
    );
  };

  const goToGroup = (groupId: string) => {
    void navigate({ to: "/app/admin/whatsapp-groups/$groupId", params: { groupId } });
  };

  // Backend-whitelisted sort columns for admin/whatsapp-groups: created_at, name.
  const columns: TColumnDef<TWhatsAppGroupListItem>[] = [
    {
      id: "name",
      header: "Group",
      sortable: true,
      sortKey: "name",
      cell: (g) => <span className="text-sm font-medium text-foreground">{g.name}</span>,
    },
    { id: "investor", header: "Investor", accessor: (g) => g.investor_company_name ?? "—" },
    { id: "fundraiser", header: "Fundraiser", accessor: (g) => g.fundraiser_company_name ?? "—" },
    {
      id: "invite_link",
      header: "Invite link",
      hideable: true,
      cell: (g) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            copyInviteLink(g.invite_link);
          }}
          className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary/80"
          title="Copy invite link"
        >
          <Copy className="h-4 w-4" />
          Copy
        </button>
      ),
    },
    { id: "created_at", header: "Created", type: "date", sortable: true, sortKey: "created_at" },
  ];

  return (
    <div className="h-full overflow-y-auto bg-background p-4 sm:p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground sm:text-3xl">WhatsApp Introduction Groups</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Groups created when investor introductions are accepted. View messages and communicate as Fundy.
          </p>
        </div>

        <DataGrid<TWhatsAppGroupListItem>
          getRowId={(row) => row.id}
          persistKey="admin.whatsapp-groups"
          ariaLabel="WhatsApp introduction groups"
          data={{ mode: "server", fetcher }}
          columns={columns}
          pagination={{ mode: "offset", pageSize: PAGE_SIZE, pageSizeOptions: [10, 20, 50, 100] }}
          toolbar={{
            search: true,
            refresh: true,
            columnVisibility: true,
            density: true,
            export: ["csv", "json"],
            searchPlaceholder: "Search group name",
          }}
          onRowClick={(g) => goToGroup(g.id)}
          rowActions={(g) => [
            { label: "View messages", onClick: () => goToGroup(g.id) },
            { label: "Copy invite link", onClick: () => copyInviteLink(g.invite_link) },
          ]}
          emptyState={{
            title: "No groups yet",
            description:
              "WhatsApp groups are created when fundraisers accept introduction requests. To enable this feature, turn on WhatsApp group creation in System Configuration and ensure both parties have verified phone numbers.",
          }}
        />
      </div>
    </div>
  );
}
